import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateSettings } from '../../base/settings/actions';
import { openSettingsDialog } from '../../settings/actions';
import { portalApi } from '../api';
import { clearSession, getAccessToken, getStoredUser, saveSession } from '../authStorage';
import { THEME_MODES } from '../constants';
import { parsePortalAuthQuery } from '../functions';
import logger from '../logger';
import { getThemeMode, saveThemeMode } from '../settings';
import AuthJitsiPage from './AuthJitsiPage';
import HomePage from './HomePage';
import LoginPage from './LoginPage';
/**
 * 门户根组件：替换默认 WelcomePage。
 * 直链换票：/?portalAuth=1&room=xxx
 */
export default function PortalApp() {
    const [view, setView] = useState('boot');
    const [user, setUser] = useState(getStoredUser());
    const dispatch = useDispatch();
    const reduxThemeMode = useSelector((state) => state['features/base/settings'].themeMode);
    const themeMigratedRef = useRef(false);
    // 旧门户弹窗只写 localStorage：若 Redux 仍是默认「跟随系统」，灌入一次门户偏好
    useEffect(() => {
        if (themeMigratedRef.current) {
            return;
        }
        themeMigratedRef.current = true;
        const stored = getThemeMode();
        if (reduxThemeMode === THEME_MODES.SYSTEM && stored !== THEME_MODES.SYSTEM) {
            dispatch(updateSettings({ themeMode: stored }));
            logger.info(`[门户] ${new Date().toISOString()} 迁移门户主题偏好到会中设置 | themeMode=${stored}`);
        }
    }, [dispatch, reduxThemeMode]);
    // 会中设置改主题后写回门户 localStorage，供「我的」页读取
    useEffect(() => {
        if (reduxThemeMode === THEME_MODES.LIGHT
            || reduxThemeMode === THEME_MODES.DARK
            || reduxThemeMode === THEME_MODES.SYSTEM) {
            saveThemeMode(reduxThemeMode);
        }
    }, [reduxThemeMode]);
    const goHomeOrLogin = useCallback(async () => {
        const auth = parsePortalAuthQuery(window.location.search);
        if (auth.isPortalAuth) {
            setView('auth-jitsi');
            return;
        }
        const token = getAccessToken();
        if (!token) {
            setUser(null);
            setView('login');
            return;
        }
        try {
            const profile = await portalApi.me(token);
            saveSession(token, profile);
            setUser(profile);
            setView('home');
            logger.info(`门户会话有效 | userId=${profile.id}`);
        }
        catch (e) {
            logger.warn(`门户会话失效 | ${e?.message}`);
            clearSession();
            setUser(null);
            setView('login');
        }
    }, []);
    useEffect(() => {
        goHomeOrLogin();
    }, [goHomeOrLogin]);
    const onOpenSettings = useCallback(() => {
        logger.info(`[门户] ${new Date().toISOString()} 打开会中通用设置对话框`);
        dispatch(openSettingsDialog(undefined, true));
    }, [dispatch]);
    const onThemeChange = useCallback((mode) => {
        saveThemeMode(mode);
        dispatch(updateSettings({ themeMode: mode }));
    }, [dispatch]);
    if (view === 'boot') {
        return React.createElement("div", { className: 'portal-boot' });
    }
    if (view === 'auth-jitsi') {
        return (React.createElement(AuthJitsiPage, { onNeedHome: () => {
                // 清掉换票参数，回到门户首页
                const url = new URL(window.location.href);
                url.search = '';
                window.history.replaceState({}, '', url.toString());
                goHomeOrLogin();
            } }));
    }
    if (view === 'login' || !user) {
        return (React.createElement(LoginPage, { onLoggedIn: () => {
                // 登录成功后统一进入门户首页（HomePage），与 Electron 客户端行为一致；
                // 不再因 portalAuth=1 自动跳转换票进会，由用户在门户首页手动选择
                // 「快速会议 / 加入会议 / 预定」等入口入会。
                const url = new URL(window.location.href);
                url.search = '';
                window.history.replaceState({}, '', url.toString());
                setUser(getStoredUser());
                setView('home');
            } }));
    }
    return (React.createElement(HomePage, { onLogout: () => {
            clearSession();
            setUser(null);
            setView('login');
        }, onOpenSettings: onOpenSettings, onThemeChange: onThemeChange, onUserUpdated: next => {
            const token = getAccessToken();
            if (token) {
                saveSession(token, next);
            }
            setUser(next);
            logger.info(`[门户] ${new Date().toISOString()} 已更新本地用户资料 | userId=${next.id}`);
        }, user: user }));
}
